import type { _RouterTyped } from 'unplugin-vue-router'
import type { RouteNamedMap } from 'vue-router/auto'
import type { IUser } from '@/pages/admin/users/types'
import { createAssinaturaStatusService } from '@/services/AssinaturaStatusService'
import { canNavigate } from '@layouts/plugins/casl'

export const setupGuards = (router: _RouterTyped<RouteNamedMap & { [key: string]: any }>) => {
  // 👉 router.beforeEach
  // Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
  router.beforeEach(async to => {
    /*
     * If it's a public route, continue navigation. This kind of pages are allowed to visited by login & non-login users. Basically, without any restrictions.
     * Examples of public routes are, 404, under maintenance, etc.
     */
    if (to.meta.public)
      return

    /**
     * Check if user is logged in by checking if token & user data exists in local storage
     * Feel free to update this logic to suit your needs
     */
    const isLoggedIn = !!(useCookie<IUser>('userData').value && useCookie('accessToken').value)
    const auth = useCookie<IUser>('userData').value

    if (isLoggedIn && !auth?.email_verified_at && to.name !== 'admin-auth-verificar-email')
      return { name: 'admin-auth-verificar-email' }

    // Verificar assinatura ativa para fornecedores e espaços de eventos
    if (isLoggedIn
      && (
        (typeof auth?.role === 'object' && (auth?.role?.slug === 'fornecedor' || auth?.role?.slug === 'espaco-eventos'))
        || (typeof auth?.role === 'string' && (auth?.role === 'fornecedor' || auth?.role === 'espaco-eventos'))
      )
      && to.name !== 'admin-selecionar-plano-id'
      && to.name !== 'admin-minhas-assinaturas'
    ) {
      try {
        const assinaturaService = createAssinaturaStatusService()
        const temAssinaturaAtiva = await assinaturaService.verificarAssinaturaAtiva()

        if (!temAssinaturaAtiva) {
          // Se não tem assinatura ativa, verificar se tem fornecedores e redirecionar para seleção de plano
          const userFornecedores = useCookie('userFornecedores').value

          if (userFornecedores && Array.isArray(userFornecedores) && userFornecedores.length > 0) {
            const fornecedor = userFornecedores[0] as any

            return { name: 'admin-selecionar-plano-id', params: { id: fornecedor.id } }
          }
        }
      }
      catch (error) {
        console.error('Erro ao verificar assinatura:', error)
      }
    }

    /*
      If user is logged in and is trying to access login like page, redirect to home
      else allow visiting the page
      (WARN: Don't allow executing further by return statement because next code will check for permissions)
     */
    if (to.meta.unauthenticatedOnly) {
      if (isLoggedIn)
        return '/'
      else
        return undefined
    }

    // Verificar permissões
    if (!canNavigate(to) && to.matched.length) {
      if (isLoggedIn) {
        // Se for organizador sem permissão, redirecionar para pesquisa avançada
        const userRole = auth?.role
        const isOrganizador = typeof userRole === 'object' && userRole?.slug === 'organizador'

        return isOrganizador
          ? { name: 'admin-pesquisa-avancada' }
          : { name: 'not-authorized' }
      }
      else {
        return {
          name: 'admin-auth-login',
          query: {
            ...to.query,
            to: to.fullPath !== '/' ? to.path : undefined,
          },
        }
      }
    }
  })
}
